import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { closestCenter, DndContext, KeyboardSensor, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { ActionIcon, Flex, Stack, Text } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { Add } from "@mui/icons-material";
import { SvgIcon } from "@mui/material";
import { useEffect, useState } from "react";
import { useBulkReorderPriorityViewsMutation, useGetPriorityViewsQuery } from "../../graphql/graphql";
import { DeletePriorityViewModal } from "./DeletePriorityViewModal";
import { EditTableViewModal } from "./EditPriorityViewModal";
import { SortablePriorityView } from "./SortablePriorityView";
export const PriorityOrderView = ({ opened }) => {
    const [{ data }, refetchPriorityViews] = useGetPriorityViewsQuery({
        requestPolicy: "cache-and-network",
        pause: !opened
    });
    const priorityViews = data?.priorityViews?.priorityViews ?? [];
    const priorityStatuses = data?.priorityStatuses?.priorityStatuses ?? [];
    // State
    const [priorityView, setPriorityView] = useState(null);
    const [priorityViewState, setPriorityViewState] = useState((priorityViews ?? []));
    const [, bulkReorderPriorityViews] = useBulkReorderPriorityViewsMutation();
    // Modals
    const [priorityViewDeleteModalOpened, { open: priorityViewDeleteModalOpen, close: priorityViewDeleteModalClose }] = useDisclosure(false);
    const [editTableViewModalOpened, { open: editTableViewModalOpen, close: editTableViewModalClose }] = useDisclosure(false);
    // Update the priority status state when the priority statuses are fetched
    useEffect(() => {
        setPriorityViewState((priorityViews ?? []));
    }, [priorityViews]);
    const sensors = useSensors(useSensor(PointerSensor), useSensor(KeyboardSensor, {
        coordinateGetter: sortableKeyboardCoordinates
    }));
    const handleClickAndDrag = (event) => {
        const { active, over } = event;
        if (!active || !over)
            return;
        if (active.id === over.id) {
            return;
        }
        handleDragEnd(active, over);
    };
    const handleDragEnd = async (active, over) => {
        setPriorityViewState((items) => {
            const oldIndex = items.findIndex((item) => item?.priorityViewId === active.id);
            const newIndex = items.findIndex((item) => item?.priorityViewId === over.id);
            const newItems = arrayMove(items, oldIndex, newIndex);
            // Create reindexed array with the new order
            const reindexedViews = newItems
                .map((view, index) => {
                if (!view)
                    return null;
                return {
                    entityId: view.priorityViewId,
                    sortOrder: index
                };
            })
                .filter((view) => view !== null);
            bulkReorderPriorityViews({
                input: reindexedViews
            });
            return newItems;
        });
    };
    const handleClickEdit = (priorityView) => {
        // Open the edit priority view modal
        if (!priorityView) {
            setPriorityView(null);
            editTableViewModalOpen();
        }
        else {
            setPriorityView(priorityView);
            editTableViewModalOpen();
        }
    };
    const handleClickDelete = (priorityView) => {
        setPriorityView(priorityView);
        priorityViewDeleteModalOpen();
    };
    const handleCloseEditTableViewModal = () => {
        setPriorityView(null);
        editTableViewModalClose();
    };
    return (_jsxs(Stack, { children: [_jsxs(Flex, { justify: "space-between", align: "center", style: { marginTop: "1rem", marginBottom: "1rem" }, children: [_jsx(Text, { variant: "body-strong", children: "Drag to reorder views" }), _jsx(ActionIcon, { variant: "filled", onClick: () => handleClickEdit(), children: _jsx(SvgIcon, { component: Add, fontSize: "inherit" }) })] }), _jsx(DndContext, { sensors: sensors, collisionDetection: closestCenter, onDragEnd: handleClickAndDrag, children: _jsx(SortableContext, { items: priorityViewState?.map((view) => view?.priorityViewId) ?? [], strategy: verticalListSortingStrategy, children: priorityViewState?.map((priorityView) => (_jsx(SortablePriorityView, { priorityView: priorityView, onClickEdit: handleClickEdit, onClickDelete: handleClickDelete }, priorityView?.priorityViewId))) }) }), priorityView && (_jsx(DeletePriorityViewModal, { priorityView: priorityView, opened: priorityViewDeleteModalOpened, close: priorityViewDeleteModalClose })), _jsx(EditTableViewModal, { opened: editTableViewModalOpened, close: handleCloseEditTableViewModal, priorityStatusList: priorityStatuses, refetchPriorityViews: refetchPriorityViews, defaultView: priorityView })] }));
};
